import React, { HTMLProps, ReactNode, useEffect, useState } from "react";

import classnames from "classnames";

import { Body2 } from "Cobalt/BodyText";
import { Div } from "Common/HTML";
import Icon, { type IconProps } from "Common/Icon";

import type { StyleProps } from "Utils/styleProps";

import makeBem from "Utils/makeBem";

const bem = makeBem("HorizontalLoading");

export interface HorizontalLoadingProps extends StyleProps, HTMLProps<HTMLDivElement> {
  /** The loading text shown */
  title?: string;
  /** Number of loading dot icons to show */
  dots?: number;
  /** Interval that the active dot changes at */
  interval?: number;
  /** (optional) Provide a boolean value to render the loader conditionally */
  isLoading?: boolean;
  /** (Optional) Render children if isLoading=false */
  children?: ReactNode | any;
  /** (Optional) Provide props for the circular icons rendered */
  iconProps?: Partial<IconProps>;
}

export const HorizontalLoading = ({
  title = "Loading\u2026",
  isLoading,
  dots = 4,
  interval = 200,
  children = null,
  className,
  iconProps,
  ...props
}: HorizontalLoadingProps) => {
  const [activeDot, setActiveDot] = useState(0);

  useEffect(() => {
    // Only run the effect if isLoading is true
    if (!isLoading) return;

    // Cycle through dots on the specified interval
    const intervalId = setInterval(() => {
      setActiveDot((prevActiveDot) => (prevActiveDot + 1) % dots);
    }, interval);

    // Clear interval on unmount - perf optimization
    return () => clearInterval(intervalId);
  }, [isLoading, dots, interval]);

  if (isLoading === false) {
    return children;
  }
  return (
    <Div className={classnames(bem(), className)} my={4} {...props}>
      <span className="sr-only">{title || "Loading\u2026"}</span>
      {Array.from({ length: dots }, (_, i) => (
        <Icon
          key={i}
          icon="circle"
          flavor="solid"
          size="sm"
          mx={1}
          {...iconProps}
          className={classnames(bem("Icon"), iconProps?.className, {
            [bem("Icon", "active")]: i === activeDot,
          })}
        />
      ))}
      {title && (
        <Body2 mt={4} color="muted" weight="bold">
          {title}
        </Body2>
      )}
    </Div>
  );
};
